/* eslint-disable jsx-a11y/no-distracting-elements */
import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { loadTranslations } from '../i18n';
import { isMobile, isTablet } from 'react-device-detect';
import FeatherIcon from 'feather-icons-react';
import { notification } from 'antd';

import { API_URL, ENDPOINTS } from '../constants/api';
import { decodeToken, getToken } from '../utils/TokenUtils';

import ModalForm from './ModalForm';
import TopNavbarStatement from './TopNavbarStatement';
import InfoModal from './InfoModal';
import ReceiptForm from './ReceiptForm';
import PasswordWarning from '../pages/users/PasswordWarning';
import LayoutSwitcher from './LayoutSwitcher';

const MyNavbar = () => {
  const [screenSize, setScreenSize] = useState('large');
  const { t } = useTranslation();
  const [showNotifications, setShowNotifications] = useState(false);
  const isPhone = isMobile && !isTablet;

  const navigate = useNavigate();
  const token = getToken();
  const searchRef = useRef(null);

  const userId = decodeToken(token, 'id');
  const userImage = decodeToken(token, 'image');
  const userName = decodeToken(token, 'name');
  const companyName = decodeToken(token, 'companyName');
  const companyLogo = decodeToken(token, 'logo');
  const loginPage = decodeToken(token, 'loginPage');
  const userType = decodeToken(token, 'userType');
  const userlevel = decodeToken(token, 'userLevel');
  const dashboardPage = decodeToken(token, 'dashboardPage');
  const passwordExpireLeft = decodeToken(token, 'passwordExpireLeft');

  const [isNavBarOpen, setIsNavBarOpen] = useState(false);
  const [isNavBarOpen2, setIsNavBarOpen2] = useState(false);

  const [filteredLinks, setFilteredLinks] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [selectedNotification, setSelectedNotification] = useState({});

  const [categories, setCategories] = useState([]);
  const [links, setLinks] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalProps, setModalProps] = useState({
    userId: null,
    token: null,
    route: null,
  });

  const isSamaCompany = API_URL?.includes('samacompany');

  useEffect(() => {
    loadTranslations();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setResults([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (query.trim().length === 0) {
      setResults([]);
      return;
    }

    if (!token) {
      navigate('/login/user-login');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${API_URL}/${ENDPOINTS.SYS}/search-link?query=${encodeURIComponent(query)}`,
          {
            headers: { Authorization: token ? `Bearer ${token}` : '' },
          }
        );
        setResults(response.data || []);
      } catch (error) {
        console.error('Error fetching search details:', error);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(fetchData, 250);
    return () => clearTimeout(timer);
  }, [query, token, navigate]);

  const handleSelectedNotification = async (id, index) => {
    try {
      const payload = {
        notification_id: id,
        read_user_id: userId,
        user_level: userlevel,
      };
      await axios.post(`${API_URL}/${ENDPOINTS.SYS}/create/make-read-message`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowNotifications(false);
      setSelectedNotification(notifications[index]);
    } catch (error) {
      console.error('Error updating notification state:', error);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/login/user-login');
      return;
    }

    const fetchNotification = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/${ENDPOINTS.SYS}/reportCustom/unread-notifications`,
          { user_level: userlevel, user_id: userId },
          { headers: { Authorization: token ? `Bearer ${token}` : '' } }
        );

        const data = response.data?.data || [];
        setNotifications(data);
        setUnreadNotifications(data[0]?.count || 0);
      } catch (error) {
        // Handle silently
      }
    };

    fetchNotification();
  }, [selectedNotification, token, userId, userlevel, navigate]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 576) setScreenSize('small');
      else if (window.innerWidth < 1592) setScreenSize('medium');
      else setScreenSize('large');
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let linksUrl;
    if (
      (companyName?.toLowerCase().includes('imaam') && companyName?.toLowerCase().includes('shaafici')) ||
      (API_URL === 'https://newktcapi.kaafi.net' && userType === 'customer') ||
      API_URL === 'https://sitcoscollegeapi.kaafi.net' || API_URL==='https://imischoolapi.ktc.so'
    ) {
      linksUrl = `${API_URL}/${ENDPOINTS.SYS}/get-categories2`;
    } else {
      linksUrl = `${API_URL}/${ENDPOINTS.SYS}/get-categories`;
    }

    const fetchLinks = async () => {
      try {
        const response = await axios.get(linksUrl, {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        });
        setCategories(response.data?.categories || []);
        setLinks(response.data?.links || []);
      } catch (error) {
        notification.error({
          message: `Error ${error?.response?.status || '500'}: ${
            error?.response?.data?.message || 'Failed to sync categories'
          }`,
        });
      }
    };

    const fetchLanguages = async () => {
      try {
        const response = await axios.get(`${API_URL}/${ENDPOINTS.USER}/get-languages`);
        setLanguages(response.data || []);
      } catch (error) {
        console.error('Error fetching system languages:', error);
      }
    };

    fetchLinks();
    fetchLanguages();
  }, [companyName, userType, token]);

  const toggleNavbar = () => setIsNavBarOpen(!isNavBarOpen);
  const toggleNavbar2 = () => setIsNavBarOpen2(!isNavBarOpen2);
  const closeNavbar = () => setIsNavBarOpen(false);
  const closeNavbar2 = () => setIsNavBarOpen2(false);

  const handleNavigation = (e, category, path) => {
    e.preventDefault();
    closeNavbar();
    const categoryLinks = links.filter((link) => link.category_id === category.category_id);
    setActiveCategory(category);
    setFilteredLinks(categoryLinks);
    navigate(path);
  };

  const onClickSearch = (e, path, route, tab) => {
    e.preventDefault();
    setQuery('');
    setResults([]);
    if (path.includes('-modal/')) {
      setModalProps({ userId, token, route });
      setIsModalOpen(true);
    } else {
      if (tab === 'new-tab') {
        const fullPath = path.startsWith('http') ? path : `${window.location.origin}/${path}`;
        window.open(fullPath, '_blank', 'noopener,noreferrer');
      } else {
        navigate(path);
      }
    }
  };

  const handleOpenModal = (e, route) => {
    if (e) e.preventDefault();
    setModalProps({ userId, token, route });
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      await axios.get(`${API_URL}/api/users/logout`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('authToken');
      navigate(`/login/${loginPage}`);
    }
  };

  const mainLinks = categories;
  const currentLanguage = localStorage.getItem('language');

  const handleLanguageChange = (event) => {
    localStorage.setItem('language', event.target.value);
    window.location.reload();
  };

  const otherSubLinks = filteredLinks.filter((link) => link.sub_category === 'Others');
  const distinctSubCategories = [
    ...new Set(
      filteredLinks
        .filter((link) => link.sub_category !== 'Others')
        .map((link) => link.sub_category)
    ),
  ];

  const hasSubNav = filteredLinks && filteredLinks.length > 0;
  const showPasswordWarning = passwordExpireLeft && parseInt(passwordExpireLeft, 10) > 0;
  const fixedHeaderRef = useRef(null);
const [headerHeight, setHeaderHeight] = useState(100);

useEffect(() => {
  if (!fixedHeaderRef.current) return;

  const updateHeight = () => {
    if (fixedHeaderRef.current) {
      setHeaderHeight(fixedHeaderRef.current.offsetHeight);
    }
  };

  // Initial measurement
  updateHeight();

  // ResizeObserver automatically handles text wrapping & window resize
  const resizeObserver = new ResizeObserver(() => updateHeight());
  resizeObserver.observe(fixedHeaderRef.current);

  return () => resizeObserver.disconnect();
}, [categories, showPasswordWarning]);

  return (
    <>
      <style>{`
  /* Fixed Header Container (Main Header ONLY) */
  .fixed-top-wrapper {
    z-index: 1030;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  }

  /* Top Utility Strip */
  .top-info-bar {
    background-color: #0b1120;
    border-bottom: 1px solid #1e293b;
    color: #94a3b8;
    font-size: 0.8125rem;
  }

  /* Main Navbar Area */
  .sidebar-matched-header {
    background-color: #0f172a !important;
    border-bottom: 1px solid #1e293b;
  }

  /* Category Navigation Wrapper */
  .navbar-nav.mx-auto {
    flex-wrap: wrap !important;
    justify-content: center;
    max-width: 100%;
  }

  /* Category Button Pill */
  .nav-link-pill {
    color: #cbd5e1;
    font-weight: 500;
    font-size: 0.85rem;
    padding: 0.35rem 0.65rem;
    border-radius: 8px;
    transition: all 0.2s ease-in-out;
    border: 1px solid transparent;
    white-space: nowrap;
  }

  .nav-link-pill:hover {
    color: #ffffff !important;
    background-color: #334155 !important;
    border-color: #475569;
  }

  .nav-link-pill.active-pill {
    color: #ffffff !important;
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important;
    font-weight: 600;
    border-color: #60a5fa;
    box-shadow: 0 3px 10px rgba(37, 99, 235, 0.45);
  }

  .category-pill-content {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    white-space: nowrap;
  }

  .user-controls-container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
    margin-left: auto;
  }

  .search-container-dark {
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 8px;
    transition: all 0.2s ease;
  }

  .search-container-dark:focus-within {
    background: #0f172a;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
  }

  /* Normal Scrolling Sub-Category Bar (Non-Fixed) */
  .subnav-dark-scrollable {
    background-color: #1e293b;
    border-bottom: 1px solid #334155;
    position: relative;
    z-index: 1020;
    margin-bottom: 1rem;
  }

  .subnav-dark-scrollable .navbar-nav {
    flex-wrap: wrap !important;
    row-gap: 0.25rem;
  }

  .subnav-dark-scrollable .subnav-title {
    color: #94a3b8;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.04em;
  }

  .subnav-dark-scrollable .nav-link {
    color: #e2e8f0;
    font-size: 0.85rem;
    font-weight: 500;
    padding: 0.35rem 0.75rem;
    border-radius: 6px;
    transition: all 0.15s ease;
  }

  .subnav-dark-scrollable .nav-link:hover {
    color: #ffffff;
    background-color: #334155;
  }

  /* Fixed Height Spacer for ONLY the top header */
  .main-header-spacer {
    width: 100%;
  }

  .badge-pulse {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
    animation: pulse 1.6s infinite;
  }

  @keyframes pulse {
    70% { box-shadow: 0 0 0 6px rgba(239, 68, 68, 0); }
    100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
  }

  /* ===================================================== */
  /* ===== SAMA THEME (same as old design)           ===== */
  /* ===================================================== */

  /* Top bar: navy */
  .sama-theme .top-info-bar {
    background-color: #232950;
    border-bottom: none;
    color: #fff;
  }
  .sama-theme .top-info-bar select {
    background-color: #fff !important;
    color: #000 !important;
    border: 1px solid #ccc !important;
    border-radius: 4px;
  }

  /* Main navbar: light */
  .sama-theme .sidebar-matched-header,
  .sama-theme.sidebar-matched-header {
    background-color: #f8f9fa !important;
    border-bottom: none;
  }
  .sama-theme .sidebar-matched-header .text-white,
  .sama-theme .sidebar-matched-header .text-slate-300,
  .sama-theme .sidebar-matched-header .text-slate-400,
  .sama-theme .sidebar-matched-header .text-slate-200 {
    color: #232950 !important;
  }
  .sama-theme .navbar-brand {
    color: #000 !important;
  }

  /* Category pills */
  .sama-theme .nav-link-pill {
    color: #232950;
    font-weight: 500;
  }
  .sama-theme .nav-link-pill:hover {
    color: #232950 !important;
    background-color: rgba(240, 128, 28, 0.2) !important;
    border-color: transparent;
  }
  .sama-theme .nav-link-pill.active-pill {
    color: #232950 !important;
    background: #F0801C !important;
    font-weight: 700;
    border-color: #F0801C;
    box-shadow: none;
  }

  /* Search */
  .sama-theme .search-container-dark {
    background: #fff;
    border: 1px solid #ccc;
  }
  .sama-theme .search-container-dark input {
    color: #212529 !important;
  }
  .sama-theme .search-container-dark:focus-within {
    background: #fff;
    border-color: #F0801C;
    box-shadow: 0 0 0 3px rgba(240, 128, 28, 0.3);
  }

  /* Bell, user button, dropdown panels (inline #1e293b) */
  .sama-theme [style*="1e293b"],
  .sama-theme [style*="rgb(30, 41, 59)"] {
    background-color: #fff !important;
    border-color: #ddd !important;
  }
  .sama-theme .dropdown-menu .dropdown-item,
  .sama-theme .dropdown-menu .text-white {
    color: #232950 !important;
  }
  .sama-theme .dropdown-menu .dropdown-item:hover {
    background-color: rgba(240, 128, 28, 0.2);
  }
  .sama-theme .btn-primary {
    background-color: #F0801C;
    border-color: #F0801C;
    color: #232950;
  }
  .sama-theme .btn-outline-light {
    color: #232950;
    border-color: #232950;
  }

  /* Sub-nav: orange */
  .subnav-dark-scrollable.sama-theme {
    background-color: #F0801C;
    border-bottom: none;
  }
  .subnav-dark-scrollable.sama-theme .subnav-title {
    color: #232950 !important;
    text-transform: none !important;
    font-size: 1.1rem;
    font-weight: 400;
    letter-spacing: 0;
  }
  .subnav-dark-scrollable.sama-theme .nav-link {
    color: #232950;
    font-weight: 700;
  }
  .subnav-dark-scrollable.sama-theme .nav-link:hover {
    color: #fff;
    background-color: #232950;
  }
`}</style>
      {/* ONLY Main Header is Fixed at Top */}
<div className={`fixed-top fixed-top-wrapper ${isSamaCompany ? 'sama-theme' : ''}`} ref={fixedHeaderRef}>
        {/* Top Utility Bar */}
        <div className="top-info-bar d-flex align-items-center justify-content-between px-3 px-lg-4 py-1">
          <TopNavbarStatement />
          <div className="d-flex align-items-center gap-3">
            <LayoutSwitcher />
            <div className="d-flex align-items-center gap-1.5">
              <select
                className="form-select form-select-sm border-0 bg-secondary bg-opacity-25 text-white shadow-none"
                value={currentLanguage || ''}
                onChange={handleLanguageChange}
                style={{ width: '100px', fontSize: '13.5px', height: '34px' }}
              >
                {languages?.map((lang) => (
                  <option key={lang.id} value={lang.id} className="text-dark">
                    {t(lang.name)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Primary Navbar */}
        <nav className={`navbar navbar-expand-lg sidebar-matched-header py-2 mb-0 ${isSamaCompany ? 'sama-theme' : ''}`}>
          <div className="container-fluid px-3 px-lg-4">
            <img
              src={`${API_URL}/${companyLogo}`}
              width="45px"
              height="45px"
              alt="Company Logo"
              className="rounded-circle border border-slate-700 shadow-sm object-fit-cover flex-shrink-0"
            />{' '}
            &nbsp;
            <a
              className="navbar-brand  text-white me-3"
              title={`${window.location.origin}/${dashboardPage}`}
              href={`${window.location.origin}/${dashboardPage?.replace('/', '')}`}
            >
              {companyName}
            </a>

            {/* Quick Search */}
            {userType === 'user' && (
              <div className="position-relative me-auto d-none d-md-block" style={{ width: '220px' }} ref={searchRef}>
                <div className="search-container-dark d-flex align-items-center px-2.5 py-1">
                  <FeatherIcon icon="search" size="15" className="text-slate-400 me-2" />
                  <input
                    type="text"
                    className="form-control border-0 bg-transparent shadow-none p-0 text-white"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('Search shortcuts...')}
                    style={{ fontSize: '0.85rem' }}
                  />
                  {loading && <div className="spinner-border spinner-border-sm text-primary ms-1" role="status" />}
                </div>

                {results && results.length > 0 && (
                  <div
                    className="position-absolute mt-2 w-100 bg-slate-800 rounded-3 shadow-lg border border-slate-700 p-2 overflow-auto"
                    style={{ maxHeight: '360px', zIndex: 1050, backgroundColor: '#1e293b' }}
                  >
                    <div className="list-group list-group-flush">
                      {results.map((item, index) => (
                        <div key={index} className="list-group-item list-group-item-action bg-transparent border-0 text-white rounded-2 p-2 mb-1">
                          <div className="fw-semibold text-white small">{t(item.name)}</div>
                          <div className="d-flex gap-2 mt-1.5">
                            <button
                              className="btn btn-sm btn-primary rounded-2 py-0.5 px-2.5"
                              style={{ fontSize: '0.75rem' }}
                              onClick={(e) => onClickSearch(e, item.full_route, item.route, 'current-tab')}
                            >
                              Open
                            </button>
                            <button
                              className="btn btn-sm btn-outline-light rounded-2 py-0.5 px-2.5"
                              style={{ fontSize: '0.75rem' }}
                              onClick={(e) => onClickSearch(e, item.full_route, item.route, 'new-tab')}
                            >
                              New Tab
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <button className="navbar-toggler border-0 shadow-none p-1 text-white" type="button" onClick={toggleNavbar}>
              <FeatherIcon icon={isNavBarOpen ? 'x' : 'menu'} size="22" />
            </button>

            {/* Navigation Categories */}
            <div className={`collapse navbar-collapse ${isNavBarOpen ? 'show' : ''}`} id="navbarNav">
              <ul className="navbar-nav mx-auto gap-1 my-2 my-lg-0 d-flex flex-wrap justify-content-center">
                {mainLinks?.map((link, index) => {
                  const isActive = activeCategory?.category_id === link.category_id;
                  return (
                    <li className="nav-item" key={index}>
                      <button
                        type="button"
                        className={`nav-link border-0 bg-transparent nav-link-pill ${isActive ? 'active-pill' : ''}`}
                        onClick={(e) => handleNavigation(e, link, `/${link.full_route}`)}
                      >
                        <span className="category-pill-content">
                          <FeatherIcon icon={link.category_icon || 'grid'} size="15" className="flex-shrink-0" />
                          <span>{t(link.category)}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              {/* User Controls */}
              <div className="user-controls-container pt-2 pt-lg-0 border-top border-slate-800 border-lg-0">
                <div className="position-relative">
                  <button
                    className="btn border-0 rounded-circle p-0 d-flex align-items-center justify-content-center shadow-sm"
                    style={{ width: '36px', height: '36px', backgroundColor: '#1e293b' }}
                    onClick={() => setShowNotifications(!showNotifications)}
                    title={t('Notifications')}
                  >
                    <FeatherIcon icon="bell" size="16" className="text-slate-300" />
                    {unreadNotifications > 0 && (
                      <span className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-slate-900 rounded-circle badge-pulse">
                        <span className="visually-hidden">notifications</span>
                      </span>
                    )}
                  </button>

                  {showNotifications && (
                    <div
                      className="position-absolute end-0 mt-2 bg-slate-800 rounded-3 shadow-lg border border-slate-700 p-2.5"
                      style={{ width: '300px', zIndex: 1050, backgroundColor: '#1e293b' }}
                    >
                      <div className="d-flex align-items-center justify-content-between p-2 border-bottom border-slate-700 mb-1">
                        <span className="fw-semibold text-white fs-6">{t('Notifications')}</span>
                        <span className="badge bg-blue-600 text-white rounded-pill px-2.5 py-1">
                          {unreadNotifications} new
                        </span>
                      </div>
                      <div className="overflow-auto" style={{ maxHeight: '250px' }}>
                        {notifications?.length > 0 ? (
                          notifications.map((notif, index) => (
                            <button
                              key={notif.id || index}
                              className="dropdown-item text-wrap p-2 rounded-2 small text-start mb-1 text-slate-200"
                              onClick={() => handleSelectedNotification(notif.id, index)}
                            >
                              <div className="fw-medium text-white">{notif.title}</div>
                            </button>
                          ))
                        ) : (
                          <div className="text-center text-slate-400 small py-4">{t('No new notifications')}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="dropdown">
                  <button
                    className="btn d-flex align-items-center gap-2 rounded-pill px-2 py-1 border-0 shadow-sm"
                    style={{ backgroundColor: '#1e293b' }}
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <img
                      src={`${API_URL}/${userImage}`}
                      alt="User"
                      className="rounded-circle object-fit-cover border border-slate-700"
                      width="28"
                      height="28"
                    />
                    <span className="fw-semibold text-white me-1" style={{ fontSize: '0.85rem' }}>{userName}</span>
                    <FeatherIcon icon="chevron-down" size="14" className="text-slate-400" />
                  </button>
                  <ul className="dropdown-menu dropdown-menu-dark dropdown-menu-end shadow-lg border-slate-700 rounded-3 mt-2 p-2" style={{ backgroundColor: '#1e293b' }}>
                    <li>
                      <Link className="dropdown-item rounded-2 small d-flex align-items-center gap-2 py-2" to="/profile">
                        <FeatherIcon icon="user" size="15" className="text-slate-400" />
                        {t('Profile')}
                      </Link>
                    </li>
                    <li>
                      <button
                        className="dropdown-item rounded-2 small d-flex align-items-center gap-2 py-2 border-0 bg-transparent w-100 text-start"
                        onClick={(e) => handleOpenModal(e, 'change-password')}
                      >
                        <FeatherIcon icon="key" size="15" className="text-slate-400" />
                        {t('Change Password')}
                      </button>
                    </li>
                    {userlevel === 'developer' && (
                      <>
                        <li><hr className="dropdown-divider border-slate-700 my-1" /></li>
                        <li>
                          <Link
                            className="dropdown-item rounded-2 small d-flex align-items-center gap-2 py-2 text-warning"
                            to="/dashboard"
                          >
                            <FeatherIcon icon="database" size="15" />
                            {t('Backup')}
                          </Link>
                        </li>
                      </>
                    )}
                    <li><hr className="dropdown-divider border-slate-700 my-1" /></li>
                    <li>
                      <button
                        className="dropdown-item rounded-2 small d-flex align-items-center gap-2 py-2 text-danger border-0 bg-transparent w-100 text-start"
                        onClick={handleLogout}
                      >
                        <FeatherIcon icon="log-out" size="15" />
                        {t('Logout')}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </nav>

      </div>

      {/* Static Offset Spacer for Fixed Header */}
      <div
        className="main-header-spacer"
        style={{ height: `${headerHeight}px` }}
        // style={{ height: `${showPasswordWarning ? 138 : 100}px` }}
      />

      {/* Sub-Category Navigation Bar (Scrolls Normally) */}
      {hasSubNav && (
<nav className={`navbar navbar-expand-lg subnav-dark-scrollable py-1 ${isSamaCompany ? 'sama-theme' : ''}`}>
            <div className="container-fluid px-4 px-lg-4">
            <span className="navbar-brand subnav-title text-uppercase text-slate-300 me-3">
              {t(activeCategory?.category)}
            </span>
            <button className="navbar-toggler border-0 p-0 shadow-none" type="button" onClick={toggleNavbar2}>
              <FeatherIcon icon="more-horizontal" size="20" className="text-light" />
            </button>
            <div className={`collapse navbar-collapse ${isNavBarOpen2 ? 'show' : ''}`} id="navbarNav2">
              <ul className="navbar-nav gap-1 d-flex flex-wrap">
                {otherSubLinks.map((link, index) => (
                  <li className="nav-item" key={index}>
                    <Link
                      className="nav-link rounded-pill"
                      to={`/${link.full_route}`}
                      onClick={() => {
                        if (link.full_route.includes('-modal')) {
                          setModalProps({ userId, token, route: link.route });
                          setIsModalOpen(true);
                        } else {
                          closeNavbar2();
                        }
                      }}
                    >
                      {t(link.name)}
                    </Link>
                  </li>
                ))}

                {distinctSubCategories.map((subCategory, index) => (
                  <li className="nav-item dropdown" key={index}>
                    <button
                      className="nav-link dropdown-toggle bg-transparent border-0 d-flex align-items-center gap-1"
                      id={`navbarDropdown-${index}`}
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      {t(subCategory)}
                    </button>
                    <ul className="dropdown-menu dropdown-menu-dark shadow-lg border-slate-700 rounded-3 mt-1 p-2" aria-labelledby={`navbarDropdown-${index}`} style={{ backgroundColor: '#1e293b' }}>
                      {filteredLinks
                        .filter((subLink) => subLink.sub_category === subCategory)
                        .map((subLink, subIndex) => (
                          <li key={subIndex}>
                            <Link
                              className="dropdown-item rounded-2 py-1.5"
                              style={{ fontSize: '0.85rem' }}
                              to={`/${subLink.full_route}`}
                              onClick={() => {
                                if (subLink.full_route.includes('-modal')) {
                                  setModalProps({ userId, token, route: subLink.route });
                                  setIsModalOpen(true);
                                } else {
                                  setIsModalOpen(false);
                                  closeNavbar2();
                                }
                              }}
                            >
                              {t(subLink.name)}
                            </Link>
                          </li>
                        ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </nav>
      )}
      {/* Password Warning */}
        <PasswordWarning passwordExpireLeft={passwordExpireLeft} handleOpenModal={handleOpenModal} />

      {/* Modals & Popups */}
      {selectedNotification?.id && (
        <InfoModal
          key={selectedNotification.id}
          title={selectedNotification.title}
          info={selectedNotification.message}
          onclose={() => setSelectedNotification({})}
        />
      )}

      {isModalOpen && modalProps?.route && (
        modalProps.route === 'new-receipt2' || modalProps.route === 'new-bus-fee-receipt' ? (
          <ReceiptForm
            userId={modalProps.userId}
            token={modalProps.token}
            route={modalProps.route}
            isOpen={isModalOpen}
            onRequestClose={closeModal}
          />
        ) : (
          <ModalForm
            userId={modalProps.userId}
            token={modalProps.token}
            route={modalProps.route}
            isOpen={isModalOpen}
            onRequestClose={closeModal}
          />
        )
      )}
    </>
  );
};

export default MyNavbar;